import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { Component, Input, ViewChild, AfterViewInit, inject, OnInit } from '@angular/core';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule, DatePipe } from '@angular/common';
import { MatTooltip } from "@angular/material/tooltip";
import { StateService } from '@adgate/common/services/state-service';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatRippleModule } from '@angular/material/core';
import { TvCommercialEditDialogComponent, TvCommercialEditDialogResult } from '@adgate/modules/tv/dialogs/tv-commercial-edit-dialog-component/tv-commercial-edit-dialog-component';
import { CommercialFilter } from '@adgate/modules/tv/types/commercial-filter.type';
import { Commercial } from '@adgate/modules/tv/types/commecial.type';
import { TvService } from '../../services/tv-service';
import { DialogService } from '@adgate/common/services/dialog-service';
import { AuthenticationService } from '@adgate/common/services/authentication-service';
import { Privilege } from '@adgate/modules/admin/types/privilege.type';
import { Status } from '@adgate/common/types/status.type';
import { switchMap, filter, throwError, finalize } from 'rxjs';
import { AdGateConstants } from '@adgate/common/constants/adgate.constants';
import { UploadDialogComponent, UploadDialogResult } from '@adgate/common/components/upload-dialog-component/upload-dialog-component';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import * as XLSX from 'xlsx';
import { MediaPlayerDialogData } from '@adgate/common/components/media-player-dialog-component/media-player-dialog-component';
import { environment } from '@env/environment';
import { QuickSearch } from '@adgate/common/interfaces/quick-search.interface';
import { Export } from '@adgate/common/interfaces/export.interface';
import { Filter } from '@adgate/common/interfaces/filter.interface';
import { TvCommercialFiltersDialogComponent } from '@adgate/modules/tv/dialogs/tv-commercial-filters-dialog-component/tv-commercial-filters-dialog-component';
import { SpecCheckReportDialogComponent } from '@adgate/common/components/spec-check-report-dialog-component/spec-check-report-dialog-component';

@Component({
  selector: 'tv-commercial-grid-component',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCheckboxModule,
    MatTooltip,
    ClipboardModule,
    MatRippleModule,
    MatSnackBarModule,
    ObjectDescPipe
  ],
  providers: [
    DatePipe
  ],
  templateUrl: './tv-commercial-grid-component.html',
  styleUrl: './tv-commercial-grid-component.scss',
})
export class TvCommercialGridComponent implements OnInit, AfterViewInit, QuickSearch, Export, Filter
{
  private state: StateService = inject(StateService);
  private tvService: TvService = inject(TvService);
  private dialogService: DialogService = inject(DialogService);
  private datePipe: DatePipe = inject(DatePipe);

  @Input() description: string = "Blank";
  @Input() filter: CommercialFilter | null = null;
  @Input() refreshOnInit: boolean = false;
  @Input() showUploadedDate: boolean = false;
  @Input() allowFiltering: boolean = false;

  private readonly FILTER_STORAGE_KEY = 'adgate.tv.filter';

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  stateService: StateService = inject( StateService );
  authService: AuthenticationService = inject( AuthenticationService );
  _snackBar: MatSnackBar = inject(MatSnackBar);

  displayedColumns: string[] = ['refcode', 'brand', 'reftitle', 'agency', 'status', 'type', 'created', 'actions'];
  dataSource: MatTableDataSource<Commercial>;

  allowEditHistory: boolean = false;
  allowEditCommercial: boolean = false;
  allowDownloadProxy: boolean = false;

  constructor() 
  {
    this.dataSource = new MatTableDataSource<Commercial>([]);
  }

  ngOnInit(): void
  {
    this.initPrivileges();

    if (this.filter === null || this.filter === undefined) {
      this.filter = this.state.loadFilter(this.FILTER_STORAGE_KEY, CommercialFilter) ?? new CommercialFilter();
    }

    if( this.refreshOnInit ) {
      this.onRefresh();
    } else {
      this.dialogService.hideProgress();
    }
  }

  ngAfterViewInit()
  {
    this.dataSource.sortingDataAccessor = this.getTableDataAccessor();
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getQuickSearchPlaceholder(): string 
  {
    return "Search Commercials";
  }

  onQuickSearch(filter: string | null)
  {
    this.dataSource.filter = filter!;
  }
  
  onRefresh()
  {
    this.dialogService.showProgress();
    this.tvService.getCommercials(this.filter!).subscribe({
      next: (r: RESTApplicationResponse) => {
        this.dataSource.data = r.data;
        this.dialogService.hideProgress();
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog( err.message );
      }
    });
  }

  showFilters(): boolean 
  {
    return this.allowFiltering;
  }

  onFilter()
  {
    const dialogRef = this.dialogService.openAnyDialog(TvCommercialFiltersDialogComponent, {
      data: this.filter!,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if ( result ) {
        this.filter = result;
        this.state.saveFilter(this.FILTER_STORAGE_KEY, this.filter!);
        this.onRefresh();
      }
    });
  }

  onViewHistory(row: Commercial): void
  {
    this.dialogService.openHistoryDialog(
      Commercial.OBJECT_TYPE,
      row.id,
      `${row.brand}/${row.product}`,
      row.status,
      this.allowEditHistory
    );
  }

  allowUploadCommercial(c: Commercial): boolean
  {
    return this.allowEditCommercial && (c.status.id == Status.TV_CREATED || c.status.id == Status.TV_SPECERROR || c.status.id == Status.TV_REJECTED);
  }

  allowViewProxy(c: Commercial): boolean
  {
    return (c.status.id == Status.TV_PROCESSED || c.status.id == Status.TV_APPROVED);
  }

  onViewProxy(row: Commercial): void
  {
    let media: MediaPlayerDialogData = {
      mediaUrl: `${environment.streamProxyUrl}${row.referenceCode}?x-auth-token=${atob(environment.idHash)}&x-user-token=${this.state.data.userToken}`,
      identifier: row.referenceCode!,
      objectType: Commercial.OBJECT_TYPE,
      objectId: row.id,
      mediaDownloadAllowed: this.allowDownloadProxy,
      startOfMedia: row.startOfMedia
    }

    this.dialogService.openMediaPlayerDialog( media );
  }

  allowDeleteCommercial(c: Commercial): boolean
  {
    return this.allowEditCommercial && (c.status.id == Status.TV_CREATED || c.status.id == Status.TV_SPECERROR);
  }

  onDeleteCommercial(row: Commercial): void
  {
    const dialogRef = this.dialogService.openConfirmDialog(
      `Delete commercial "${row.referenceCode}"?`,
      {
        title: 'Delete Commercial',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        severity: 'danger'
      }
    );

    dialogRef.afterClosed().pipe(
      filter((confirmed) => confirmed === true),
      switchMap(() => {
        return this.tvService.deleteCommercial(row.id);
      })
    ).subscribe({
      next: (r: RESTApplicationResponse) => {
        this.onRefresh();
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onNewCommercial(): void
  {
    const dialogRef = this.dialogService.openAnyDialog(TvCommercialEditDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result: TvCommercialEditDialogResult | null) => (result !== null) ),
      switchMap((result:TvCommercialEditDialogResult) => {
        if( result.uploadMethod.id == AdGateConstants.UPLOADMETHOD_HTTP ) {
          return this.tvService.createAndUploadCommercial_HTTP(result.commercial, result.media);
        } else if( result.uploadMethod.id == AdGateConstants.UPLOADMETHOD_ASPERA ) {
          this.dialogService.showProgress();
          return this.tvService.createAndUploadCommercial_ASPERA(result.commercial, result.media);
        } else {
          this.dialogService.showProgress();
          return this.tvService.createCommercial(result.commercial);
        }
      }),
      finalize(() => {
        this.tvService.getCommercials(this.filter!).subscribe(r => {
          this.dataSource.data = r.data;
        });
      })
    ).subscribe({
      next: (r:RESTApplicationResponse) => {
        if( (r.data as Commercial).status.id === Status.TV_SPECERROR ) {
          this.onViewSpecCheckReport(r.data as Commercial);
        } else {
          this.dialogService.openInfoDialog(r.message);
        }
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onUploadCommercial(row: Commercial): void
  {
    const dialogRef = this.dialogService.openAnyDialog(UploadDialogComponent, {
      width: '600px',
      minWidth: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result:UploadDialogResult | null) => result !== null),
      switchMap((result:UploadDialogResult) => {
        if( result.uploadMethod.id == AdGateConstants.UPLOADMETHOD_HTTP ) {
          return this.tvService.uploadCommercial_HTTP(row.id, row.referenceCode!, result.media);
        } else if( result.uploadMethod.id == AdGateConstants.UPLOADMETHOD_ASPERA ) {
          this.dialogService.showProgress();
          return this.tvService.uploadCommercial_ASPERA(row.id, row.referenceCode!, result.media);
        } else {
          return throwError(() => new Error('Invalid upload method'));
        }
      }),
      finalize(() => {
        this.tvService.getCommercials(this.filter!).subscribe(r => {
          this.dataSource.data = r.data;
        });
      })
    ).subscribe({
      next: (r:RESTApplicationResponse) => {
        if( (r.data as Status).id === Status.TV_SPECERROR ) {
          this.onViewSpecCheckReport(row);
        } else {
          this.dialogService.openInfoDialog(r.message);
        }
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onViewSpecCheckReport(c:Commercial)
  {
    const dialogRef = this.dialogService.openAnyDialog(SpecCheckReportDialogComponent,{
      data: {
        objectType: Commercial.OBJECT_TYPE,
        objectId: c.id,
        title: `${c.referenceCode}`,
        allowUpload: this.allowUploadCommercial(c),
        specsUrl: `${environment.helpCenterUrl}#specs-tv`
      },
      width: '600px',
      minWidth: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result) => result != null)
    ).subscribe((result:'upload' | 'view-specs') => {
      if( result === 'upload' ) {
        this.onUploadCommercial(c);
      }
    });
  }

  onExport()
  {
    if (this.dataSource.data.length > 0) {
      this.dialogService.showProgress();
      const jsonData = this.dataSource.data.map( (c:Commercial) => ({
        'Ref. Code': c.referenceCode,
        'Brand': c.brand,
        'Product': c.product,
        'Title': c.version,
        'Category': c.category.description,
        'Agency': c.agency?.description,
        'Office': c.commercialOffice?.description,
        'Type': c.type.description,
        'Language': c.language.description,
        'CRT': c.crt,
        'Status': c.status.description,
        'Created': this.datePipe.transform(c.dateCreated,'d/M/yyyy'),
        'Uploaded': this.datePipe.transform(c.dateUploaded,'d/M/yyyy')
      }));

      let filename = "commercial-export.xlsx";
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Commercials');
      XLSX.writeFile(wb, filename);
      this.dialogService.hideProgress();
    } else {
      this.dialogService.openErrorDialog("There are no commercials to export");
    }
  }

  onCopyRefCode(refCode: string)
  {
    this._snackBar.open( `${refCode} copied to clipboard`, '',{duration: 2000});
  }

  showTimeFlag(c: Commercial): number
  {
    if( c.status.id != Status.TV_PROCESSED && c.status.id != Status.TV_UPLOADED && c.status.id != Status.TV_RELOAD ) {
      return 0;
    }

    if (!c.dateUploaded) {
      return 0;
    }

    const now = Date.now();
    const uploadedTime = c.dateUploaded; // Convert from Unix seconds to milliseconds
    const hoursPassed = (now - uploadedTime) / (1000 * 60 * 60);

    if (hoursPassed >= 48) {
      return 48;
    } else if (hoursPassed >= 24) {
      return 24;
    } else {
      return 0;
    }
  }
  
  getTableDataAccessor(): any
  {
    return (item:any, property:any) => {
      switch(property) {
        case 'refcode': return item.referenceCode;
        case 'reftitle': return item.version;
        case 'category': return item.category.description;
        case 'agency': return item.agency.description;
        case 'language': return item.language.description;
        case 'type': return item.type.description;
        case 'created': return item.dateCreated;
        default: return item[property];
      }
    };
  }

  private initPrivileges(): void
  {
    let decodedToken = this.authService.decodeToken( this.stateService.data.userToken );
    this.allowEditHistory = this.authService.checkPrivilegeOnToken( decodedToken, [Privilege.COMM_EDITHISTORY]);
    this.allowEditCommercial = this.authService.checkPrivilegeOnToken( decodedToken, [Privilege.COMM_UPDATECOMMERCIAL]);
    this.allowDownloadProxy = this.authService.checkPrivilegeOnToken( decodedToken, [Privilege.GENE_DOWNLOADPROXY]);
  }
}
